import React from "react";

/**
 * Reusable Pagination Component
 *
 * Features:
 * - Page navigation (first, prev, current, next, last)
 * - Search input with debounce
 * - Items per page selector
 * - Display total items and current range
 *
 * @param {Object} props
 * @param {Object} props.pagination - Pagination info { page, limit, total, totalPages, hasNext, hasPrev }
 * @param {Function} props.onPageChange - Callback when page changes (page)
 * @param {Function} props.onLimitChange - Callback when limit changes (limit)
 * @param {Function} props.onSearchChange - Callback when search changes (searchTerm)
 * @param {string} props.searchValue - Current search value
 * @param {string} props.searchPlaceholder - Placeholder for search input
 * @param {boolean} props.showSearch - Whether to show search input (default: true)
 * @param {boolean} props.showLimitSelector - Whether to show limit selector (default: true)
 * @param {Array<number>} props.limitOptions - Available limit options (default: [10, 20, 50, 100])
 */
const Pagination = ({
  pagination = {},
  onPageChange,
  onLimitChange,
  onSearchChange,
  searchValue = "",
  searchPlaceholder = "Search...",
  showSearch = true,
  showLimitSelector = true,
  limitOptions = [10, 20, 50, 100],
}) => {
  const {
    page = 1,
    limit = 20,
    total = 0,
    totalPages = 1,
    hasNext = false,
    hasPrev = false,
  } = pagination;

  // Calculate display range
  const startItem = total > 0 ? (page - 1) * limit + 1 : 0;
  const endItem = Math.min(page * limit, total);

  // Generate page numbers to show
  const getPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 5;

    if (totalPages <= maxPagesToShow) {
      // Show all pages if total is small
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show smart pagination
      const halfWay = Math.ceil(maxPagesToShow / 2);

      if (page <= halfWay) {
        // Near the beginning
        for (let i = 1; i <= maxPagesToShow; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(totalPages);
      } else if (page >= totalPages - halfWay) {
        // Near the end
        pages.push(1);
        pages.push("...");
        for (let i = totalPages - maxPagesToShow + 1; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        // In the middle
        pages.push(1);
        pages.push("...");
        for (let i = page - 1; i <= page + 1; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const handleSearchChange = (e) => {
    if (onSearchChange) {
      onSearchChange(e.target.value);
    }
  };

  const handleLimitChange = (e) => {
    if (onLimitChange) {
      onLimitChange(parseInt(e.target.value));
    }
  };

  const handlePageClick = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages && onPageChange) {
      onPageChange(newPage);
    }
  };

  return (
    <div className="pagination-container">
      {/* Search and Limit Selector Row */}
      {(showSearch || showLimitSelector) && (
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mb-4">
          {/* Search Input */}
          {showSearch && (
            <div className="w-full sm:w-auto sm:flex-1 max-w-md">
              <div className="relative">
                <input
                  type="text"
                  value={searchValue}
                  onChange={handleSearchChange}
                  placeholder={searchPlaceholder}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <svg
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                </svg>
              </div>
            </div>
          )}

          {/* Items per page selector */}
          {showLimitSelector && (
            <div className="flex items-center gap-2">
              <label
                htmlFor="limit-select"
                className="text-sm text-gray-700 whitespace-nowrap"
              >
                Items per page:
              </label>
              <select
                id="limit-select"
                value={limit}
                onChange={handleLimitChange}
                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {limitOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      )}

      {/* Pagination Info and Controls */}
      <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
        {/* Results Info */}
        <div className="text-sm text-gray-700">
          Showing <span className="font-medium">{startItem}</span> to{" "}
          <span className="font-medium">{endItem}</span> of{" "}
          <span className="font-medium">{total}</span> results
        </div>

        {/* Page Navigation */}
        <div className="flex items-center gap-1">
          {/* First Page Button */}
          <button
            onClick={() => handlePageClick(1)}
            disabled={!hasPrev}
            className={`px-3 py-2 rounded-lg text-sm font-medium ${
              !hasPrev
                ? "text-gray-400 cursor-not-allowed"
                : "text-gray-700 hover:bg-gray-100"
            }`}
            title="First page"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path d="M11 19l-7-7 7-7m8 14l-7-7 7-7"></path>
            </svg>
          </button>

          {/* Previous Page Button */}
          <button
            onClick={() => handlePageClick(page - 1)}
            disabled={!hasPrev}
            className={`px-3 py-2 rounded-lg text-sm font-medium ${
              !hasPrev
                ? "text-gray-400 cursor-not-allowed"
                : "text-gray-700 hover:bg-gray-100"
            }`}
            title="Previous page"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path d="M15 19l-7-7 7-7"></path>
            </svg>
          </button>

          {/* Page Numbers */}
          {getPageNumbers().map((pageNum, index) => {
            if (pageNum === "...") {
              return (
                <span
                  key={`ellipsis-${index}`}
                  className="px-3 py-2 text-gray-400"
                >
                  ...
                </span>
              );
            }

            return (
              <button
                key={pageNum}
                onClick={() => handlePageClick(pageNum)}
                className={`px-4 py-2 rounded-lg text-sm font-medium ${
                  page === pageNum
                    ? "bg-blue-600 text-white"
                    : "text-gray-700 hover:bg-gray-100"
                }`}
              >
                {pageNum}
              </button>
            );
          })}

          {/* Next Page Button */}
          <button
            onClick={() => handlePageClick(page + 1)}
            disabled={!hasNext}
            className={`px-3 py-2 rounded-lg text-sm font-medium ${
              !hasNext
                ? "text-gray-400 cursor-not-allowed"
                : "text-gray-700 hover:bg-gray-100"
            }`}
            title="Next page"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path d="M9 5l7 7-7 7"></path>
            </svg>
          </button>

          {/* Last Page Button */}
          <button
            onClick={() => handlePageClick(totalPages)}
            disabled={!hasNext}
            className={`px-3 py-2 rounded-lg text-sm font-medium ${
              !hasNext
                ? "text-gray-400 cursor-not-allowed"
                : "text-gray-700 hover:bg-gray-100"
            }`}
            title="Last page"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path d="M13 5l7 7-7 7M5 5l7 7-7 7"></path>
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Pagination;
